import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';

import uploadIcon from '../action-menu/icon--file-upload.svg';
import collectCountIcon from '../project-gallery/collect-1.svg';
import praiseCountIcon from '../project-gallery/praise-icon-1.svg';
import previewIcon from '../project-gallery/preview.svg';
import {API_DOMAIN, http} from '../../lib/http/request';
import {loadDictOption} from '../../lib/dict-service';

import styles from './user-center.css';
import {HashPrefix} from '../../lib/hash-prefix';

const API_ORIGIN = API_DOMAIN.replace(/\/api\/?$/, '');
const PAGE_SIZE = 12;
const PROJECT_TAG_DICT_CODE = 'scratch_project_tag';
const MAX_PROJECT_SCREENSHOT_SIZE = 5 * 1024 * 1024;

const getImageSrc = image => {
    if (!image) return '';
    if (/^(data:|blob:|https?:)/.test(image)) return image;
    if (image.charAt(0) === '/') return `${API_ORIGIN}${image}`;
    return image;
};

const formatDate = value => {
    if (!value) return '暂无时间';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const normalizePageResult = response => {
    const page = response && response.result ? response.result : {};
    return {
        records: Array.isArray(page.records) ? page.records : [],
        total: Number(page.total || 0),
        pageIndex: Number(page.current || 1),
        pageSize: Number(page.size || PAGE_SIZE)
    };
};

const normalizeProjectTags = tags => {
    if (Array.isArray(tags)) {
        return tags.map(tag => String(tag || '').trim()).filter(Boolean);
    }
    return String(tags || '')
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);
};

const normalizeTagOptions = items => (
    (Array.isArray(items) ? items : [])
        .map(item => {
            const value = item.itemValue || item.value || item.id || '';
            const label = item.itemText || item.label || item.name || value;
            return {
                color: item.itemColor || '',
                label,
                value
            };
        })
        .filter(item => item.value && item.label)
);

const getCountText = value => String(Number(value || 0));

const getPreviewUrl = project => {
    return `/player.html#${HashPrefix.PROJECT}${project.id}`;
};

class ProjectsPage extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleCancelDelete',
            'handleCancelEdit',
            'handleConfirmDelete',
            'handleEditNameChange',
            'handleEditRemarkChange',
            'handleEditOpenChange',
            'handleEditScreenshotChange',
            'handleEditScreenshotPick',
            'handleLoadMore',
            'handleNextPage',
            'handleOpenProject',
            'handlePrevPage',
            'handlePreviewProject',
            'handleRefresh',
            'handleScroll',
            'handleStartDelete',
            'handleStartEdit',
            'handleSubmitEdit',
            'handleTouchEnd',
            'handleTouchMove',
            'handleTouchStart',
            'handleToggleTag',
            'loadProjectTags',
            'loadProjects',
            'setScreenshotInput'
        ]);
        this.state = {
            deleting: false,
            deletingProject: null,
            deleteStatus: '',
            editingProject: null,
            editName: '',
            editOpen: false,
            editScreenshot: '',
            editStatus: '',
            editTags: [],
            error: '',
            hasMore: true,
            loadingTags: false,
            loading: false,
            pageIndex: 1,
            pageSize: PAGE_SIZE,
            projects: [],
            projectTagOptions: [],
            pullDistance: 0,
            refreshing: false,
            savingEdit: false,
            tagError: '',
            total: 0,
            uploadingScreenshot: false
        };
        this.mounted = false;
        this.pullStartY = null;
        this.screenshotInput = null;
    }
    componentDidMount () {
        this.mounted = true;
        this.loadProjectTags();
        this.loadProjects(1);
    }
    componentWillUnmount () {
        this.mounted = false;
    }
    setScreenshotInput (input) {
        this.screenshotInput = input;
    }
    loadProjectTags () {
        this.setState({
            loadingTags: true,
            tagError: ''
        });
        loadDictOption(PROJECT_TAG_DICT_CODE)
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '标签加载失败');
                }
                this.setState({
                    projectTagOptions: normalizeTagOptions(response.result),
                    loadingTags: false
                });
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    projectTagOptions: [],
                    loadingTags: false,
                    tagError: error.message || '标签加载失败'
                });
            });
    }
    loadProjects (pageIndex, append) {
        this.setState({
            loading: true,
            error: '',
            refreshing: !append && pageIndex === 1
        });
        http.get('/scratchProject/myPageList', {
            pageIndex,
            pageSize: this.state.pageSize
        })
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '作品列表加载失败');
                }
                const page = normalizePageResult(response);
                const hasMore = page.total ?
                    page.pageIndex * page.pageSize < page.total :
                    page.records.length >= page.pageSize;
                this.setState(prevState => ({
                    hasMore,
                    projects: append ? prevState.projects.concat(page.records) : page.records,
                    total: page.total,
                    pageIndex: page.pageIndex,
                    pageSize: page.pageSize
                }));
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState(prevState => ({
                    hasMore: false,
                    projects: append ? prevState.projects : [],
                    total: append ? prevState.total : 0,
                    error: error.message || '作品列表加载失败'
                }));
            })
            .then(() => {
                if (!this.mounted) return;
                this.setState({
                    loading: false,
                    pullDistance: 0,
                    refreshing: false
                });
            });
    }
    handlePrevPage () {
        if (this.state.pageIndex <= 1 || this.state.loading) return;
        this.loadProjects(this.state.pageIndex - 1);
    }
    handleNextPage () {
        const pageCount = Math.max(1, Math.ceil(this.state.total / this.state.pageSize));
        if (this.state.pageIndex >= pageCount || this.state.loading) return;
        this.loadProjects(this.state.pageIndex + 1);
    }
    handleRefresh () {
        if (this.state.loading) return;
        this.loadProjects(1);
    }
    handleLoadMore () {
        if (this.state.loading || !this.state.hasMore) return;
        this.loadProjects(this.state.pageIndex + 1, true);
    }
    handleScroll (event) {
        if (this.state.loading || !this.state.hasMore) return;
        const {scrollTop, scrollHeight, clientHeight} = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight <= 160) {
            this.handleLoadMore();
        }
    }
    handleTouchStart (event) {
        if (event.currentTarget.scrollTop > 0 || event.touches.length !== 1) return;
        this.pullStartY = event.touches[0].clientY;
    }
    handleTouchMove (event) {
        if (this.pullStartY === null || event.touches.length !== 1) return;
        const deltaY = event.touches[0].clientY - this.pullStartY;
        if (deltaY <= 0) {
            this.setState({pullDistance: 0});
            return;
        }
        this.setState({pullDistance: Math.min(deltaY, 80)});
    }
    handleTouchEnd () {
        const shouldRefresh = this.state.pullDistance >= 56 && !this.state.loading;
        this.pullStartY = null;
        if (shouldRefresh) {
            this.loadProjects(1);
            return;
        }
        this.setState({pullDistance: 0});
    }
    handleStartDelete (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        const project = this.state.projects.find(item => item.id === projectId);
        if (!project) return;
        this.setState({
            deletingProject: project,
            deleteStatus: ''
        });
    }
    handleCancelDelete () {
        if (this.state.deleting) return;
        this.setState({
            deletingProject: null,
            deleteStatus: ''
        });
    }
    handleConfirmDelete () {
        if (!this.state.deletingProject) return;
        this.setState({
            deleting: true,
            deleteStatus: ''
        });
        http.delete('/scratchProject/my', {
            ids: this.state.deletingProject.id
        })
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '作品删除失败');
                }
                this.setState({
                    deleting: false,
                    deletingProject: null,
                    deleteStatus: ''
                }, () => {
                    this.loadProjects(1);
                });
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    deleting: false,
                    deleteStatus: error.message || '作品删除失败'
                });
            });
    }
    handleStartEdit (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        const project = this.state.projects.find(item => item.id === projectId);
        if (!project) return;
        this.setState({
            editingProject: project,
            editName: project.projectName || '',
            editRemark: project.remark || '',
            editOpen: Boolean(project.isOpen),
            editScreenshot: project.screenshot || '',
            editStatus: '',
            editTags: normalizeProjectTags(project.tags),
            uploadingScreenshot: false
        });
    }
    handleCancelEdit () {
        if (this.state.savingEdit || this.state.uploadingScreenshot) return;
        this.setState({
            editingProject: null,
            editName: '',
            editRemark: '',
            editOpen: false,
            editScreenshot: '',
            editStatus: '',
            editTags: [],
            uploadingScreenshot: false
        });
    }
    handleEditNameChange (event) {
        this.setState({
            editName: event.currentTarget.value,
            editStatus: ''
        });
    }
    handleEditRemarkChange (event) {
        console.log(event)
        this.setState({
            editRemark: event.currentTarget.value,
            editStatus: ''
        });
    }
    handleEditOpenChange (event) {
        this.setState({
            editOpen: event.currentTarget.checked,
            editStatus: ''
        });
    }
    handleEditScreenshotPick () {
        if (this.screenshotInput) {
            this.screenshotInput.click();
        }
    }
    handleEditScreenshotChange (event) {
        const file = event.currentTarget.files && event.currentTarget.files[0];
        if (!file) return;
        event.currentTarget.value = '';
        if (file.type.indexOf('image') < 0) {
            this.setState({editStatus: '请选择图片格式的缩略图'});
            return;
        }
        if (file.size > MAX_PROJECT_SCREENSHOT_SIZE) {
            this.setState({editStatus: '缩略图图片不能超过 5MB'});
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', file.name);

        this.setState({
            editStatus: '',
            uploadingScreenshot: true
        });
        http.post('/upload', formData)
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '缩略图上传失败');
                }
                const fileInfo = response && response.result ? response.result : {};
                const screenshot = fileInfo.url || '';
                if (!screenshot) {
                    throw new Error('缩略图上传失败');
                }
                this.setState({
                    editScreenshot: screenshot,
                    uploadingScreenshot: false
                });
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    editStatus: error.message || '缩略图上传失败',
                    uploadingScreenshot: false
                });
            });
    }
    handleToggleTag (event) {
        const tagValue = event.currentTarget.getAttribute('data-tag-value');
        if (!tagValue) return;
        this.setState(prevState => {
            const selected = prevState.editTags.indexOf(tagValue) !== -1;
            return {
                editStatus: '',
                editTags: selected ?
                    prevState.editTags.filter(tag => tag !== tagValue) :
                    prevState.editTags.concat(tagValue)
            };
        });
    }
    handleSubmitEdit (event) {
        event.preventDefault();
        const projectName = this.state.editName.trim();
        if (!projectName) {
            this.setState({editStatus: '请填写作品名称'});
            return;
        }
        if (this.state.uploadingScreenshot) {
            this.setState({editStatus: '缩略图上传完成后再保存'});
            return;
        }
        if (!this.state.editingProject) return;
        this.setState({
            savingEdit: true,
            editStatus: ''
        });
        http.put('/scratchProject/myInfo', {
            id: this.state.editingProject.id,
            projectName,
            remark:this.state.editRemark,
            isOpen: this.state.editOpen,
            screenshot: this.state.editScreenshot,
            tags: this.state.editTags
        })
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '作品信息保存失败');
                }
                const updatedProject = response && response.result ?
                    response.result :
                    Object.assign({}, this.state.editingProject, {
                        projectName,
                        isOpen: this.state.editOpen,
                        screenshot: this.state.editScreenshot,
                        tags: this.state.editTags
                    });
                this.setState(prevState => ({
                    editingProject: null,
                    editName: '',
                    editRemark: '',
                    editOpen: false,
                    editScreenshot: '',
                    editStatus: '',
                    editTags: [],
                    projects: prevState.projects.map(project => (
                        project.id === updatedProject.id ? updatedProject : project
                    )),
                    savingEdit: false,
                    uploadingScreenshot: false
                }));
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    editStatus: error.message || '作品信息保存失败',
                    savingEdit: false
                });
            });
    }
    handleOpenProject (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        if (!projectId) return;
        window.location.hash = HashPrefix.PROJECT + projectId;
        this.props.onRequestClose();
    }
    handlePreviewProject (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        const project = this.state.projects.find(item => item.id === projectId);
        const previewUrl = getPreviewUrl(project);
        if (!previewUrl) return;
        const previewWindow = window.open(previewUrl, '_blank', 'noopener,noreferrer');
        if (previewWindow) {
            previewWindow.opener = null;
        }
    }
    getTagLabel (tagValue) {
        const option = this.state.projectTagOptions.find(item => item.value === tagValue);
        return option ? option.label : tagValue;
    }
    renderProjectTags (project) {
        const tags = normalizeProjectTags(project.tags);
        if (tags.length === 0) return null;
        const visibleTags = tags.slice(0, 3);
        return (
            <div className={styles.projectTags}>
                {visibleTags.map(tag => (
                    <span
                        className={styles.projectTag}
                        key={tag}
                    >
                        {this.getTagLabel(tag)}
                    </span>
                ))}
                {tags.length > visibleTags.length ? (
                    <span className={styles.projectTagMore}>
                        {`+${tags.length - visibleTags.length}`}
                    </span>
                ) : null}
            </div>
        );
    }
    renderProjectCard (project) {
        const screenshot = getImageSrc(project.screenshot);
        const projectName = project.projectName || '未命名作品';
        const openApplyPending = Boolean(project.openApplyPending);
        const visibilityClassName = [
            styles.projectVisibility,
            openApplyPending ? styles.projectVisibilityPending : '',
            !project.isOpen && !openApplyPending ? styles.projectVisibilityPrivate : ''
        ].filter(Boolean).join(' ');
        const visibilityText = openApplyPending ? '审核中' : (project.isOpen ? '公开' : '私有');
        return (
            <article
                className={styles.projectCard}
                key={project.id}
            >
                <button
                    className={styles.projectCoverButton}
                    data-project-id={project.id}
                    type="button"
                    onClick={this.handleOpenProject}
                >
                    {screenshot ? (
                        <img
                            className={styles.projectCover}
                            src={screenshot}
                        />
                    ) : (
                        <div className={styles.projectCoverFallback}>
                            {projectName.charAt(0)}
                        </div>
                    )}
                </button>
                <div className={styles.projectBody}>
                    <div className={styles.projectName}>{projectName}</div>
                    <div className={styles.projectMeta}>
                        {`更新于 ${formatDate(project.updateTime || project.createTime)}`}
                    </div>
                    {this.renderProjectTags(project)}
                    <div className={styles.projectFooter}>
                        <div className={styles.projectStats}>
                            <span
                                className={visibilityClassName}
                            >
                                {visibilityText}
                            </span>
                            <span className={styles.projectMetric}>
                                <img
                                    className={styles.projectMetricIcon}
                                    src={praiseCountIcon}
                                />
                                {getCountText(project.likeCount)}
                            </span>
                            <span className={styles.projectMetric}>
                                <img
                                    className={styles.projectMetricIcon}
                                    src={collectCountIcon}
                                />
                                {getCountText(project.favoriteCount)}
                            </span>
                        </div>
                        <div className={styles.projectActions}>
                            <button
                                className={styles.projectPreviewButton}
                                data-project-id={project.id}
                                disabled={!project.filePath}
                                title={project.filePath ? '预览' : '暂无预览文件'}
                                type="button"
                                onClick={this.handlePreviewProject}
                            >
                                <img
                                    className={styles.projectActionIcon}
                                    src={previewIcon}
                                />
                            </button>
                            <button
                                className={styles.projectDeleteButton}
                                data-project-id={project.id}
                                type="button"
                                onClick={this.handleStartDelete}
                            >
                                {'删除'}
                            </button>
                            <button
                                className={styles.projectEditButton}
                                data-project-id={project.id}
                                type="button"
                                onClick={this.handleStartEdit}
                            >
                                {'编辑'}
                            </button>
                            <button
                                className={styles.projectOpenButton}
                                data-project-id={project.id}
                                type="button"
                                onClick={this.handleOpenProject}
                            >
                                {'打开'}
                            </button>
                        </div>
                    </div>
                </div>
            </article>
        );
    }
    renderTagOptions () {
        if (this.state.projectTagOptions.length === 0) {
            return (
                <div className={styles.projectTagEmpty}>
                    {this.state.loadingTags ? '正在读取标签' : (this.state.tagError || '暂无可选标签')}
                </div>
            );
        }
        return (
            <div className={styles.projectTagOptions}>
                {this.state.projectTagOptions.map(option => {
                    const selected = this.state.editTags.indexOf(option.value) !== -1;
                    const className = [
                        styles.projectTagOption,
                        selected ? styles.projectTagOptionActive : ''
                    ].filter(Boolean).join(' ');
                    const colorStyle = option.color ? {backgroundColor: option.color} : {};
                    return (
                        <button
                            className={className}
                            data-tag-value={option.value}
                            key={option.value}
                            type="button"
                            onClick={this.handleToggleTag}
                        >
                            <span
                                className={styles.tagOptionColor}
                                style={colorStyle}
                            />
                            <span>{option.label}</span>
                        </button>
                    );
                })}
            </div>
        );
    }
    renderEditDialog () {
        if (!this.state.editingProject) return null;
        const screenshot = getImageSrc(this.state.editScreenshot);
        const projectName = this.state.editName || this.state.editingProject.projectName || '未命名作品';
        const showOpenApplyHint = !this.state.editingProject.isOpen;
        const openApplyHint = this.state.editingProject.openApplyPending ?
            '开放申请审核中' :
            '提交后进入开放审核';
        return (
            <div className={styles.projectEditBackdrop}>
                <form
                    className={styles.projectEditDialog}
                    onSubmit={this.handleSubmitEdit}
                >
                    <div className={styles.projectEditTitle}>{'编辑作品信息'}</div>
                    <div className={styles.projectScreenshotEditor}>
                        <button
                            className={styles.projectScreenshotButton}
                            disabled={this.state.uploadingScreenshot}
                            type="button"
                            onClick={this.handleEditScreenshotPick}
                        >
                            {screenshot ? (
                                <img
                                    className={styles.projectScreenshotImage}
                                    src={screenshot}
                                />
                            ) : (
                                <span className={styles.projectScreenshotFallback}>
                                    {projectName.charAt(0)}
                                </span>
                            )}
                            <span className={styles.projectScreenshotOverlay}>
                                <img
                                    className={styles.uploadIcon}
                                    src={uploadIcon}
                                />
                                {this.state.uploadingScreenshot ? '上传中' : '更换缩略图'}
                            </span>
                        </button>
                        <input
                            accept="image/*"
                            aria-label="选择作品缩略图"
                            className={styles.projectScreenshotFileInput}
                            ref={this.setScreenshotInput}
                            type="file"
                            onChange={this.handleEditScreenshotChange}
                        />
                    </div>
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>{'作品名称'}</span>
                        <input
                            className={styles.textInput}
                            maxLength="20"
                            placeholder="填写作品名称"
                            value={this.state.editName}
                            onChange={this.handleEditNameChange}
                        />
                    </label>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>{'作品标签'}</span>
                        {this.renderTagOptions()}
                    </div>
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>{'作品描述'}</span>
                        <textarea
                            className={styles.textarea}
                            maxLength="500"
                            rows={10}
                            placeholder="填写作品描述"
                            value={this.state.editRemark}
                            onChange={this.handleEditRemarkChange}
                        />
                    </label>
                    <label className={styles.projectOpenToggle}>
                        <input
                            checked={this.state.editOpen}
                            type="checkbox"
                            onChange={this.handleEditOpenChange}
                        />
                        <span className={styles.toggleTrack}>
                            <span className={styles.toggleThumb} />
                        </span>
                        <span className={styles.toggleText}>{'公开展示作品'}</span>
                    </label>
                    {showOpenApplyHint ? (
                        <div className={styles.projectOpenHint}>
                            {openApplyHint}
                        </div>
                    ) : null}
                    <div className={styles.projectEditStatus}>
                        {this.state.editStatus}
                    </div>
                    <div className={styles.projectEditActions}>
                        <button
                            className={styles.secondaryButton}
                            disabled={this.state.savingEdit}
                            type="button"
                            onClick={this.handleCancelEdit}
                        >
                            {'取消'}
                        </button>
                        <button
                            className={styles.primaryButton}
                            disabled={this.state.savingEdit || this.state.uploadingScreenshot}
                            type="submit"
                        >
                            {this.state.savingEdit ? '保存中' : '保存'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }
    renderDeleteDialog () {
        if (!this.state.deletingProject) return null;
        const projectName = this.state.deletingProject.projectName || '未命名作品';
        return (
            <div className={styles.projectEditBackdrop}>
                <div className={styles.projectEditDialog}>
                    <div className={styles.projectEditTitle}>{'删除作品'}</div>
                    <div className={styles.confirmText}>
                        {`确定删除「${projectName}」吗？删除后不可恢复。`}
                    </div>
                    <div className={styles.projectEditStatus}>
                        {this.state.deleteStatus}
                    </div>
                    <div className={styles.projectEditActions}>
                        <button
                            className={styles.secondaryButton}
                            disabled={this.state.deleting}
                            type="button"
                            onClick={this.handleCancelDelete}
                        >
                            {'取消'}
                        </button>
                        <button
                            className={styles.dangerButton}
                            disabled={this.state.deleting}
                            type="button"
                            onClick={this.handleConfirmDelete}
                        >
                            {this.state.deleting ? '删除中' : '删除'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    render () {
        const pullActive = this.state.pullDistance >= 56;
        return (
            <section className={styles.projectsPage}>
                <div className={styles.projectsToolbar}>
                    <div>
                        <div className={styles.projectsTitle}>{'我的作品'}</div>
                        <div className={styles.projectsSummary}>
                            {this.state.loading ?
                                '正在读取作品' :
                                `共 ${this.state.total} 个作品`}
                        </div>
                    </div>
                    <button
                        className={styles.secondaryButton}
                        disabled={this.state.loading}
                        type="button"
                        onClick={this.handleRefresh}
                    >
                        {'刷新'}
                    </button>
                </div>
                <div
                    className={styles.projectsScroll}
                    onScroll={this.handleScroll}
                    onTouchEnd={this.handleTouchEnd}
                    onTouchMove={this.handleTouchMove}
                    onTouchStart={this.handleTouchStart}
                >
                    {this.state.pullDistance > 0 ? (
                        <div
                            className={styles.pullStatus}
                            style={{height: `${this.state.pullDistance}px`}}
                        >
                            {pullActive ? '松开刷新' : '下拉刷新'}
                        </div>
                    ) : null}
                    {this.state.error ? (
                        <div className={styles.projectsError}>
                            {this.state.error}
                        </div>
                    ) : null}
                    {!this.state.loading && !this.state.error && this.state.projects.length === 0 ? (
                        <div className={styles.emptyProjects}>
                            <div className={styles.emptyProjectsTitle}>{'还没有保存作品'}</div>
                            <div className={styles.emptyProjectsText}>{'完成一次保存后，作品会出现在这里。'}</div>
                        </div>
                    ) : null}
                    {this.state.projects.length > 0 ? (
                        <div className={styles.projectGrid}>
                            {this.state.projects.map(project => this.renderProjectCard(project))}
                        </div>
                    ) : null}
                    <div className={styles.loadMoreStatus}>
                        {this.state.loading ?
                            (this.state.refreshing ? '正在刷新' : '正在加载更多') :
                            (this.state.projects.length > 0 ?
                                (this.state.hasMore ? '上滑加载更多' : '没有更多作品了') :
                                '')}
                    </div>
                </div>
                {this.renderEditDialog()}
                {this.renderDeleteDialog()}
            </section>
        );
    }
}

ProjectsPage.propTypes = {
    onRequestClose: PropTypes.func
};

ProjectsPage.defaultProps = {
    onRequestClose: () => {}
};

export default ProjectsPage;
